import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

const DUR = 340; // ms — keep in sync with the transition below

/**
 * Telegram-style pushed screen: slides in from the right when `open`, slides
 * back out to the right when closed (content latched during the exit so it
 * doesn't vanish mid-slide). The transform is dropped once settled so any
 * position:fixed / sticky children inside resolve to the viewport again.
 */
export function PushScreen({ open, children }: { open: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(open);
  const [slidIn, setSlidIn] = useState(open);
  const [animating, setAnimating] = useState(false);
  const latched = useRef<ReactNode>(null);
  if (open) latched.current = children;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setAnimating(true);
      const r = requestAnimationFrame(() => setSlidIn(true));
      // Fallback: always drop the transform after the slide so position:fixed /
      // sticky children resolve to the viewport even if transitionEnd is missed.
      const t = window.setTimeout(() => setAnimating(false), DUR + 80);
      return () => {
        cancelAnimationFrame(r);
        window.clearTimeout(t);
      };
    }
    if (mounted) {
      setAnimating(true);
      setSlidIn(false);
      const t = window.setTimeout(() => {
        setMounted(false);
        setAnimating(false);
      }, DUR);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!mounted) return null;

  const style: CSSProperties = animating
    ? {
        transform: slidIn ? 'translateX(0)' : 'translateX(100%)',
        transition: `transform ${DUR}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        boxShadow: '-12px 0 34px rgba(0,0,0,0.16)',
      }
    : {};

  return (
    <div
      className="fixed inset-0 z-40 overflow-y-auto overscroll-none bg-surface"
      style={style}
      onTransitionEnd={(e) => {
        if (e.target === e.currentTarget && e.propertyName === 'transform' && slidIn) {
          setAnimating(false); // settled — drop the transform/containing-block
        }
      }}
    >
      {open ? children : latched.current}
    </div>
  );
}
